import { type RefObject, useEffect, useState, useMemo } from "react";
import { useTheme } from "styled-components";
import throttle from "lodash/throttle";
import Debug from "../../../../../common-deprecated/Debug";
import { loadImage, sendJsonToSalesman } from "../../../../../common-deprecated/utils";
import { useBnbSelector } from "../../redux/store";
import { SALESMAN_3D_INTERIOR_POSITION } from "../../../../../common-deprecated/constants";
import { isMaster, isSlave } from "../../../../../common-deprecated/settings/utils/commonSettingUtils";
import useWebGLSupportLevel from "../customize/useWebGLSupportLevel";
import useInteriorSpin3D from "../../../../shared-logic/hooks/useInteriorSpin3D";
import { ImageType, useCarImage } from "../gradeEngine/useCarImage";
import useGetBuildSpinnerFrame from "./spin/useGetBuildSpinnerFrame";
import { SteeringPosition } from "../../../../shared-logic/types/CarType";

type InteriorSpinType = {
    loading: boolean; // preloading images
    initialized: boolean; // scene is working
    dragged: boolean; // when user has interacted with scene, resets when disabled becomes false
};

// Loading of images and build specific event handling
const useInteriorSpin = (
    canvas: RefObject<HTMLCanvasElement>,
    enabled: boolean,
    fullscreen: boolean,
    getByFrame: boolean, // When the interior spin needs to use the modernized getFrame function instead of the classic getImage call for it's frames
    gradeId?: string,
    bodyTypeId?: string,
): InteriorSpinType => {
    const [loading, setLoading] = useState(true);
    const settings = useBnbSelector((state) => state.commonSettings);
    const model = useBnbSelector((state) => state.model);
    const salesmanOverride = useBnbSelector((state) => state.utils.salesmanOverrides.current3DInteriorPosition);
    const webGLSupportLevel = useWebGLSupportLevel();
    const isMasterSetting = isMaster(settings);
    const isSlaveSetting = isSlave(settings);
    const steering = useBnbSelector((state) => state.car.model.steering);

    // -------------------------------------------
    // Load images
    // -------------------------------------------

    // Use a lower image quality on mobile, as the effect isn't that great
    // there is no need to rerender with higher quality images if the user resizes.
    const { breakpoints } = useTheme();
    const imageQuality = typeof window !== "undefined" && window.innerWidth < parseInt(breakpoints.lg) ? 50 : 75;

    // Only the original resolution of 1280 does not gives seams,
    // but IE11 only supports sizes with a power of 2 so we use 1024 instead and request
    // it via the compression service to get rid of the seams, this results in a much larger filesize somehow
    // but that is the least of IE11 users worries.
    const { getFrame } = useCarImage();
    const { getImage, cacheKey: getImageCacheKey } = useGetBuildSpinnerFrame(false, ImageType.Interior3D);
    const cacheKey = !getByFrame ? getImageCacheKey : `${model.defaultCarId}-${model.id}-${model.configVersion}`;
    const useCompressed = webGLSupportLevel === 1;

    // This is a IE11 specific fallback for when the compressedCcisPath is not yet set.
    // Instead of using the service to resize the image, we let CCIS handle it themselves causing seams to be shown on IE11 until the env var is set.
    const imageSize = useCompressed && !settings.compressedCcisPath ? 1024 : 1280;

    const imageUrls = useMemo(() => {
        if (!enabled) return [];

        // generate image urls
        const frames = [3, 1, 5, 4, 0, 2]; // order for threejs cubetexture is right, left, up, down, front, back
        const urls = frames.map((frame) =>
            getByFrame && gradeId && bodyTypeId
                ? getFrame({
                      width: imageSize,
                      height: imageSize,
                      frame,
                      options: {
                          imageQuality,
                          useCompressed,
                          resizeDimensions: useCompressed ? { width: 1024, height: 1024 } : undefined,
                      },
                      modelId: model.id,
                      configVersion: model.configVersion,
                      gradeId,
                      bodyTypeId,
                  })
                : getImage(imageSize, imageSize, frame, {
                      imageQuality,
                      useCompressed,
                      resizeDimensions: useCompressed ? { width: 1024, height: 1024 } : undefined,
                  }),
        );

        // preload images
        setLoading(true);

        Promise.all(urls.map((src) => loadImage(src)))
            .then(() => setLoading(false))
            .catch(() => {
                Debug.info("Image loading failed");
            });

        return urls;
    }, [cacheKey, enabled, imageQuality, useCompressed]);

    // -------------------------------------------
    // Interact with scene
    // -------------------------------------------

    const { initialized, dragged, controls, updateCanvasSize, updateTexture, updatePosition, getPosition } =
        useInteriorSpin3D(canvas, enabled, {
            x: steering === SteeringPosition.Right ? -3 : 3,
            y: -1.4,
            z: 3,
            fov: 90,
        });

    // Update textures once they are preloaded or when scene is setup, whichever is first
    useEffect(() => {
        if (loading || !initialized || !imageUrls.length) return;
        updateTexture(imageUrls);
    }, [loading, initialized, imageUrls]);

    // Update canvas size on window resize (updateCanvasSize checks for and stops unneeded resizes)
    useEffect(() => {
        if (!initialized) return;
        // start render and listen for window resizes
        window.addEventListener("resize", updateCanvasSize);
        return () => window.removeEventListener("resize", updateCanvasSize);
    }, [initialized]);

    // Update canvas size when going fullscreen as this doesn't count as a window resize
    useEffect(() => {
        if (!initialized) return;
        updateCanvasSize();
    }, [fullscreen]);

    // Send angles to billboard
    useEffect(() => {
        if (!controls || !isMasterSetting) return;

        const updateBillboard: () => void = throttle(() => {
            sendJsonToSalesman({
                action: SALESMAN_3D_INTERIOR_POSITION,
                data: getPosition(),
            });
        }, 300); // needs to be greater than the animationTimeout in use3DSpin so the animation from last time has definitely finished

        controls.addEventListener("change", updateBillboard);
        return () => controls.removeEventListener("change", updateBillboard);
    }, [controls]);

    // Receive angles from salesman and update controls
    useEffect(() => {
        if (
            !isSlaveSetting ||
            salesmanOverride.x === null ||
            salesmanOverride.y === null ||
            salesmanOverride.z === null
        )
            return;
        updatePosition(salesmanOverride as any);
    }, [salesmanOverride]);

    return { loading, initialized, dragged };
};

export default useInteriorSpin;
